import React, { useEffect, useState } from "react";
import { useNavigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  LayoutDashboard, Users, Receipt, LifeBuoy, FileText,
  Package, Globe, Mail, Settings, LogOut, ShieldCheck, BarChart3, TrendingUp, FolderKanban, Wallet, Workflow, CreditCard, Bot
} from "lucide-react";
import { useCurrency } from "../lib/currency";

import AdminBlog from "./admin/AdminBlog";
import AdminProducts from "./admin/AdminProducts";
import AdminTLDs from "./admin/AdminTLDs";
import AdminLeads from "./admin/AdminLeads";
import AdminSettings from "./admin/AdminSettings";
import AdminOrders from "./admin/AdminOrders";
import AdminCustomers from "./admin/AdminCustomers";
import AdminInvoices from "./admin/AdminInvoices";
import AdminCredits from "./admin/AdminCredits";
import AdminTickets from "./admin/AdminTickets";
import AdminWorkflows from "./admin/AdminWorkflows";
import AdminPayGlocal from "./admin/AdminPayGlocal";
import AdminStaticUpi from "./admin/AdminStaticUpi";
import AdminSM888 from "./admin/AdminSM888";
import AdminDevAPI from "./admin/AdminDevAPI";
import AdminAccounting from "./admin/AdminAccounting";
import AdminAIOps from "./admin/AdminAIOps";

const NAV = [
  { id: "aiops", label: "AI Ops", icon: Bot },
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Customers", icon: Users },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "credits", label: "AI Credits", icon: Wallet },
  { id: "products", label: "Products", icon: Package },
  { id: "tlds", label: "Domains", icon: Globe },
  { id: "blog", label: "Blog CMS", icon: FileText },
  { id: "leads", label: "Leads", icon: Mail },
  { id: "tickets", label: "Tickets", icon: LifeBuoy },
  { id: "workflows", label: "Workflows", icon: Workflow },
  { id: "payglocal", label: "PayGlocal", icon: CreditCard },
  { id: "static_upi", label: "Static UPI", icon: Wallet },
  { id: "sm888", label: "SM888 Payments", icon: CreditCard },
  { id: "devapi", label: "Developer API", icon: Workflow },
  { id: "accounting", label: "Accounting", icon: TrendingUp },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { format } = useCurrency();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [section, setSection] = useState("overview");

  useEffect(() => {
    if (!user) { nav("/login?next=/admin"); return; }
    if (user.role !== "admin") { nav("/dashboard"); return; }
    Promise.all([
      api.get("/admin/stats").then(r=>r.data).catch(()=>null),
      api.get("/admin/users").then(r=>r.data).catch(()=>[]),
      api.get("/orders/all").then(r=>r.data).catch(()=>[]),
      api.get("/tickets/all").then(r=>r.data).catch(()=>[]),
    ]).then(([s,u,o,t]) => { setStats(s); setUsers(u); setOrders(o); setTickets(t); });
  }, [user, nav]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-[calc(100vh-64px)] grid grid-cols-12 gap-0">
      {/* Sidebar */}
      <aside className="col-span-12 lg:col-span-2 border-r border-slate-800 bg-slate-900/50">
        <div className="p-5 sticky top-[64px]">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white"><ShieldCheck size={18}/></div>
            <div>
              <div className="text-white font-bold leading-tight" style={{fontFamily:'Outfit'}}>Admin</div>
              <div className="text-[10px] text-blue-400 uppercase tracking-[0.18em]">Optivize Ops</div>
            </div>
          </div>
          <nav className="space-y-1" data-testid="admin-sidebar">
            {NAV.map(n => (
              <button
                key={n.id}
                onClick={()=>setSection(n.id)}
                data-testid={`admin-nav-${n.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${section === n.id ? "bg-blue-600 text-white shadow-[0_6px_20px_-6px_rgba(37,99,235,0.6)]" : "text-slate-300 hover:bg-slate-800"}`}
              >
                <n.icon size={15}/> {n.label}
              </button>
            ))}
          </nav>
          <div className="divider my-4"/>
          <button onClick={()=>{logout(); nav("/");}} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-rose-300 hover:bg-rose-500/10" data-testid="admin-logout">
            <LogOut size={15}/> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="col-span-12 lg:col-span-10 p-6 lg:p-8 max-w-[1400px]">
        {section === "overview" && stats && (
          <div className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <div className="tag mb-2">Admin Console</div>
                <h1 className="text-3xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>Welcome back, {user.name.split(" ")[0]}</h1>
                <p className="text-slate-400 text-sm">Here&apos;s what&apos;s happening across Optivize today.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {label:"Revenue", value: format(stats.revenue || 0), icon: TrendingUp, color: "from-emerald-500 to-emerald-700"},
                {label:"Paid Orders", value: stats.paid_orders, icon: Receipt, color: "from-blue-500 to-blue-700"},
                {label:"Customers", value: stats.customers, icon: Users, color: "from-violet-500 to-violet-700"},
                {label:"Open Tickets", value: stats.open_tickets, icon: LifeBuoy, color: "from-amber-500 to-amber-700"},
              ].map(s => (
                <div key={s.label} className="card p-5 relative overflow-hidden">
                  <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${s.color} opacity-20 blur-2xl`}/>
                  <div className="flex items-center gap-2 text-blue-400"><s.icon size={16}/><span className="text-xs uppercase tracking-wider">{s.label}</span></div>
                  <div className="text-3xl font-extrabold text-white mt-2" style={{fontFamily:'Outfit'}}>{s.value}</div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
              <div className="card p-5 lg:col-span-2">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><FolderKanban size={16} className="text-blue-400"/> Recent Orders</h3>
                {orders.length === 0 ? <p className="text-slate-500 text-sm">No orders yet.</p> : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
                      <tr><th className="py-2 text-left">Order</th><th className="text-left">Items</th><th className="text-right">Total</th><th className="text-right">Status</th></tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map(o => (
                        <tr key={o.id} className="border-b border-slate-800">
                          <td className="py-2 text-cyan-300 font-mono">#{o.id.slice(0,8)}</td>
                          <td className="text-slate-300 max-w-[300px] truncate">{o.items.map(i=>i.name).join(", ")}</td>
                          <td className="text-right text-white">{format(o.total)}</td>
                          <td className="text-right"><span className={o.status==="paid" ? "text-emerald-400" : "text-amber-400"}>{o.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="card p-5">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-blue-400"/> System</h3>
                <ul className="space-y-2.5 text-sm">
                  <li className="flex justify-between"><span className="text-slate-400">Users</span><span className="text-white font-semibold">{stats.users}</span></li>
                  <li className="flex justify-between"><span className="text-slate-400">Active Projects</span><span className="text-white font-semibold">{stats.active_projects}</span></li>
                  <li className="flex justify-between"><span className="text-slate-400">Total Orders</span><span className="text-white font-semibold">{stats.orders}</span></li>
                </ul>
              </div>
            </div>

            <PaymentMethodAnalyticsCard format={format}/>
          </div>
        )}

        {section === "users" && <AdminCustomers/>}

        {section === "orders" && <AdminOrders/>}

        {section === "invoices" && <AdminInvoices/>}

        {section === "credits" && <AdminCredits/>}

        {section === "products" && <AdminProducts/>}
        {section === "tlds" && <AdminTLDs/>}
        {section === "blog" && <AdminBlog/>}
        {section === "leads" && <AdminLeads/>}

        {section === "tickets" && <AdminTickets/>}

        {section === "workflows" && <AdminWorkflows/>}

        {section === "payglocal" && <AdminPayGlocal/>}
        {section === "static_upi" && <AdminStaticUpi/>}

        {section === "sm888" && <AdminSM888/>}

        {section === "devapi" && <AdminDevAPI/>}
        {section === "accounting" && <AdminAccounting/>}

        {section === "aiops" && <AdminAIOps/>}

        {section === "settings" && <AdminSettings/>}
      </main>
    </div>
  );
}


// ============================================================================
// PAYMENT METHOD ANALYTICS CARD — revenue split by rail for a rolling window.
// ============================================================================
function PaymentMethodAnalyticsCard({ format }) {
  const [window, setWindow] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.get(`/admin/payment-method-analytics?window_days=${window}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [window]);

  // Tailwind-safe palette for the bars
  const PALETTE = ["from-blue-500 to-blue-700", "from-emerald-500 to-emerald-700",
                   "from-violet-500 to-violet-700", "from-amber-500 to-amber-700",
                   "from-cyan-500 to-cyan-700", "from-rose-500 to-rose-700",
                   "from-fuchsia-500 to-fuchsia-700", "from-teal-500 to-teal-700",
                   "from-orange-500 to-orange-700", "from-pink-500 to-pink-700",
                   "from-lime-500 to-lime-700", "from-indigo-500 to-indigo-700",
                   "from-sky-500 to-sky-700", "from-yellow-500 to-yellow-700",
                   "from-purple-500 to-purple-700", "from-slate-500 to-slate-700"];

  return (
    <div className="card p-5" data-testid="payment-method-analytics-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <CreditCard size={16} className="text-blue-400"/>
          Payment-method revenue split
        </h3>
        <div className="inline-flex rounded-lg bg-slate-900/60 border border-white/5 p-1" data-testid="pma-window-toggle">
          {[7, 30, 90, 365].map(w => (
            <button key={w} onClick={() => setWindow(w)}
                    className={`px-3 py-1 rounded-md text-xs ${window === w ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
                    data-testid={`pma-window-${w}`}>
              {w === 365 ? "1y" : `${w}d`}
            </button>
          ))}
        </div>
      </div>

      {busy && <div className="text-slate-500 text-sm py-6 text-center">Loading…</div>}

      {!busy && (!data || (data.breakdown || []).length === 0) && (
        <div className="text-slate-500 text-sm py-6 text-center" data-testid="pma-empty">
          No paid invoices in the last {window} days.
        </div>
      )}

      {!busy && data && data.breakdown.length > 0 && (
        <div>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="text-3xl font-extrabold text-white" style={{fontFamily:'Outfit'}} data-testid="pma-total">
              {format(data.total_revenue)}
            </div>
            <div className="text-xs text-slate-400">
              {data.total_count} paid invoice{data.total_count === 1 ? "" : "s"} · trailing {window} days
            </div>
          </div>

          <div className="space-y-2">
            {data.breakdown.map((row, i) => (
              <div key={row.method} data-testid={`pma-row-${row.method}`}>
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="text-slate-200 font-medium flex items-center gap-1.5">
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br ${PALETTE[i % PALETTE.length]}`}/>
                    {row.label}
                    <span className="text-slate-500">· {row.count}</span>
                  </span>
                  <span className="text-slate-300 font-mono">
                    {format(row.amount)}
                    <span className="text-slate-500 ml-2">{row.percent}%</span>
                  </span>
                </div>
                <div className="w-full bg-slate-800/60 rounded-full h-1.5 overflow-hidden">
                  <div className={`h-1.5 bg-gradient-to-r ${PALETTE[i % PALETTE.length]} rounded-full`}
                       style={{width: `${Math.max(row.percent, 0.5)}%`}}/>
                </div>
              </div>
            ))}
          </div>

          {data.breakdown.length > 1 && (
            <div className="mt-4 pt-3 border-t border-slate-800 text-[11px] text-slate-500">
              Top rail: <span className="text-slate-300 font-semibold">{data.breakdown[0].label}</span> at <span className="text-emerald-300 font-semibold">{data.breakdown[0].percent}%</span> · use this to renegotiate MDR with high-share acquirers, or investigate sudden drops as an outage signal.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

